import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import { resolveImageUrl } from "../utils/resolveImageUrl";
import getApiBase from "../utils/apiBase";
import "./AdminOrders.css";

const API_BASE = getApiBase();
const SHIPMENT_STATUSES = ["preparing", "shipped", "delivered"];

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const itemImage = (item) => (
  item.previewImageUrl
  || item.imageUrl
  || item.customColorImageUrl
  || (Array.isArray(item.images) ? item.images[0] : "")
);

const emptyShipment = {
  shipmentStatus: "preparing",
  trackingCarrier: "Yanwen",
  trackingNumber: "",
  trackingUrl: "",
  adminShippingNote: "",
};

const AdminOrders = () => {
  const [auth, setAuth] = useState(localStorage.getItem("adminAuth") || "");
  const [login, setLogin] = useState({ username: "", password: "" });
  const [orders, setOrders] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [search, setSearch] = useState("");
  const [shipmentDraft, setShipmentDraft] = useState(emptyShipment);
  const [statusMessage, setStatusMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const authHeaders = useMemo(
    () => (auth ? { Authorization: `Basic ${auth}` } : {}),
    [auth],
  );

  const selectedOrder = useMemo(
    () => orders.find((item) => item.id === selectedId) || orders[0] || null,
    [orders, selectedId],
  );

  const fetchOrders = useCallback(async () => {
    if (!auth) return;
    setLoading(true);
    setStatusMessage("");
    try {
      const query = search.trim() ? `?q=${encodeURIComponent(search.trim())}` : "";
      const response = await fetch(`${API_BASE}/api/admin/orders${query}`, {
        headers: authHeaders,
      });
      if (!response.ok) {
        throw new Error("Failed to load orders.");
      }
      const data = await response.json();
      const items = Array.isArray(data.items) ? data.items : [];
      setOrders(items);
      setSelectedId((current) => current || items[0]?.id || "");
    } catch (error) {
      setStatusMessage("Failed to load orders.");
    } finally {
      setLoading(false);
    }
  }, [auth, authHeaders, search]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    if (!selectedOrder) return;
    setShipmentDraft({
      shipmentStatus: selectedOrder.shipmentStatus || "preparing",
      trackingCarrier: selectedOrder.trackingCarrier || "Yanwen",
      trackingNumber: selectedOrder.trackingNumber || "",
      trackingUrl: selectedOrder.trackingUrl || "",
      adminShippingNote: selectedOrder.adminShippingNote || "",
    });
  }, [selectedOrder]);

  const handleLogin = (event) => {
    event.preventDefault();
    const token = btoa(`${login.username}:${login.password}`);
    localStorage.setItem("adminAuth", token);
    setAuth(token);
    setStatusMessage("Logged in.");
  };

  const handleLogout = () => {
    localStorage.removeItem("adminAuth");
    setAuth("");
    setLogin({ username: "", password: "" });
    setOrders([]);
    setSelectedId("");
    setStatusMessage("Logged out.");
  };

  const saveShipment = async () => {
    if (!selectedOrder) return;
    setSaving(true);
    setStatusMessage("");
    try {
      const response = await fetch(`${API_BASE}/api/admin/orders/${selectedOrder.id}/shipment`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
        body: JSON.stringify(shipmentDraft),
      });
      if (!response.ok) {
        throw new Error("Failed to update shipment.");
      }
      const data = await response.json();
      const updated = data.order;
      setOrders((current) => current.map((item) => (
        item.id === updated.id ? updated : item
      )));
      setStatusMessage("Shipment updated.");
    } catch (error) {
      setStatusMessage("Failed to update shipment.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="admin-orders">
      <Seo
        title="Admin Orders"
        description="Internal order and shipment management page."
        path="/admin/orders"
        noindex
      />
      <header className="admin-orders-header">
        <div>
          <h1>Orders</h1>
          <nav>
            <Link to="/admin">Product Admin</Link>
            <Link to="/admin/custom-requests">Custom Requests</Link>
            <Link to="/admin/custom-projects">Custom Design Projects</Link>
          </nav>
        </div>
        {auth ? <button type="button" onClick={handleLogout}>Log out</button> : null}
      </header>

      {!auth && (
        <form className="admin-orders-login" onSubmit={handleLogin}>
          <h2>Admin Login</h2>
          <label>
            Username
            <input
              type="text"
              value={login.username}
              onChange={(event) => setLogin((current) => ({ ...current, username: event.target.value }))}
            />
          </label>
          <label>
            Password
            <input
              type="password"
              value={login.password}
              onChange={(event) => setLogin((current) => ({ ...current, password: event.target.value }))}
            />
          </label>
          <button type="submit">Log in</button>
        </form>
      )}

      {auth && (
        <section className="admin-orders-layout">
          <aside className="admin-orders-list">
            <div className="admin-orders-search">
              <input
                type="search"
                value={search}
                placeholder="Search email, order ID, tracking"
                onChange={(event) => setSearch(event.target.value)}
              />
              <button type="button" onClick={fetchOrders} disabled={loading}>
                {loading ? "Loading..." : "Search"}
              </button>
            </div>
            {orders.length ? (
              orders.map((order) => (
                <button
                  type="button"
                  className={`admin-order-list-item${selectedOrder?.id === order.id ? " selected" : ""}`}
                  key={order.id}
                  onClick={() => setSelectedId(order.id)}
                >
                  <strong>{order.id}</strong>
                  <span>{order.email}</span>
                  <em>{order.shipmentStatus || "preparing"}</em>
                  <small>{formatDate(order.paidAt || order.createdAt)}</small>
                  {order.trackingNumber ? <small>{order.trackingNumber}</small> : null}
                </button>
              ))
            ) : (
              <p className="admin-orders-empty">No orders found.</p>
            )}
          </aside>

          <section className="admin-order-detail">
            {selectedOrder ? (
              <>
                <div className="admin-order-heading">
                  <div>
                    <p>Order ID</p>
                    <h2>{selectedOrder.id}</h2>
                  </div>
                  <span>{selectedOrder.status}</span>
                </div>

                <div className="admin-order-fields">
                  <div>
                    <span>Email</span>
                    <a href={`mailto:${selectedOrder.email}`}>{selectedOrder.email}</a>
                  </div>
                  <div>
                    <span>Customer name</span>
                    <strong>{selectedOrder.customerName || "N/A"}</strong>
                  </div>
                  <div>
                    <span>Country</span>
                    <strong>{selectedOrder.countryCode || "N/A"}</strong>
                  </div>
                  <div>
                    <span>Stripe session</span>
                    <strong>{selectedOrder.stripeSessionId || "N/A"}</strong>
                  </div>
                  <div>
                    <span>Paid at</span>
                    <strong>{formatDate(selectedOrder.paidAt)}</strong>
                  </div>
                </div>

                <div className="admin-shipment-editor">
                  <h3>Shipment Tracking</h3>
                  <label>
                    Status
                    <select
                      value={shipmentDraft.shipmentStatus}
                      onChange={(event) => setShipmentDraft((current) => ({ ...current, shipmentStatus: event.target.value }))}
                    >
                      {SHIPMENT_STATUSES.map((status) => (
                        <option value={status} key={status}>{status}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Carrier
                    <input
                      type="text"
                      value={shipmentDraft.trackingCarrier}
                      onChange={(event) => setShipmentDraft((current) => ({ ...current, trackingCarrier: event.target.value }))}
                    />
                  </label>
                  <label>
                    Tracking number
                    <input
                      type="text"
                      value={shipmentDraft.trackingNumber}
                      onChange={(event) => setShipmentDraft((current) => ({ ...current, trackingNumber: event.target.value }))}
                    />
                  </label>
                  <label>
                    Tracking URL
                    <input
                      type="url"
                      value={shipmentDraft.trackingUrl}
                      placeholder="https://www.yanwenexpress.com/tracking.html"
                      onChange={(event) => setShipmentDraft((current) => ({ ...current, trackingUrl: event.target.value }))}
                    />
                  </label>
                  <label className="admin-shipment-note">
                    Internal shipping note
                    <textarea
                      rows="4"
                      value={shipmentDraft.adminShippingNote}
                      onChange={(event) => setShipmentDraft((current) => ({ ...current, adminShippingNote: event.target.value }))}
                    />
                  </label>
                  <button type="button" onClick={saveShipment} disabled={saving}>
                    {saving ? "Saving..." : "Save Tracking"}
                  </button>
                </div>

                <div className="admin-order-items">
                  <h3>Items</h3>
                  {(selectedOrder.items || []).map((item) => (
                    <article className="admin-order-item" key={item.itemKey || `${selectedOrder.id}-${item.id}`}>
                      {itemImage(item) ? (
                        <img src={resolveImageUrl(itemImage(item))} alt={item.name} />
                      ) : null}
                      <div>
                        <strong>{item.name}</strong>
                        <span>{item.customColorName || item.selectedColor || item.color || "Default"}</span>
                        <span>Quantity: {item.quantity}</span>
                        <span>Price: ${Number(item.price || 0).toFixed(2)}</span>
                      </div>
                      {item.type === "custom_yoyo" ? (
                        <div className="admin-custom-item-media">
                          {item.artworkUrl ? <a href={resolveImageUrl(item.artworkUrl)} target="_blank" rel="noreferrer">Original Artwork</a> : null}
                          {item.previewImageUrl ? <a href={resolveImageUrl(item.previewImageUrl)} target="_blank" rel="noreferrer">Adjusted Preview</a> : null}
                        </div>
                      ) : null}
                    </article>
                  ))}
                </div>
              </>
            ) : (
              <p className="admin-orders-empty">Select an order to view details.</p>
            )}
          </section>
        </section>
      )}

      {statusMessage && <p className="admin-orders-status">{statusMessage}</p>}
    </main>
  );
};

export default AdminOrders;
